import React, { useCallback, useEffect } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import GET_CLASSROOM from 'graph/classroom/get-classroom-query';
import PageContainer from 'components/@layout/PageContainer';
import { Student } from 'types/schema-generated-types';
import PrintLoginCard from 'components/PrintScreenModule/LoginCards/components/LoginCard';
import Typography from '@material-ui/core/Typography';
import getClassroomPrimaryEducator from 'utilities/get-classroom-primary-eduator';
import { usePageContentLoading } from 'providers/PageContentLoadingProvider';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2.5)
    },
    header: {
      borderBottom: `2px solid ${theme.palette.common.black}`,
      marginBottom: theme.spacing(1)
    },
    title: {
      paddingBottom: theme.spacing(1),
      fontSize: theme.typography.pxToRem(28)
    }
  };
});

interface ClassroomLoginCardContainerProps {
  classroomId: number;
  product: string;
}

const ClassroomLoginCardContainer: React.FC<ClassroomLoginCardContainerProps> = ({
  classroomId,
  product
}) => {
  const classes = useStyles();
  const [{ registered }, { onElementLoaded, registerElements }] = usePageContentLoading();
  const { data, loading, error } = useQuery(GET_CLASSROOM, {
    variables: { id: classroomId }
  });
  const educatorPin = useCallback(() => getClassroomPrimaryEducator(data?.classroom)?.pin, [data]);

  const handleOnLoad = (student: Student) => (): void => {
    onElementLoaded(String(student.id));
  };

  useEffect(() => {
    if (!registered && data?.classroom?.students) {
      registerElements(data?.classroom?.students.map((x: Student) => String(x.id)));
    }
  }, [data, registerElements, registered]);

  return (
    <PageContainer loading={loading} error={error}>
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography variant="h3" className={classes.title}>
            Class Name: {data?.classroom.name}
          </Typography>
        </div>
        <div>
          {(data?.classroom?.students || []).map((student: Student) => (
            <PrintLoginCard
              key={`print-login-card-${student.id}`}
              product={product}
              firstName={student.firstName}
              lastName={student.lastName}
              educatorPin={educatorPin() || ''}
              password={student.password || ''}
              username={student.username}
              onLoad={handleOnLoad(student)}
            />
          ))}
        </div>
      </div>
    </PageContainer>
  );
};

export default ClassroomLoginCardContainer;
